import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { MenuItem } from './rms-menu-service';

export interface AppTab {
  title: string;
  url: string;
  closable?: boolean;
}

interface StoredTabs {
  tabs: AppTab[];
  activeIndex: number;
}

@Injectable({
  providedIn: 'root',
})
export class TabService {
  private readonly router = inject(Router);
  private readonly storageKey = 'rms-open-tabs';

  private readonly homeTab: AppTab = {
    title: 'Property',
    url: '/property',
    closable: false,
  };

  private readonly tabsSubject = new BehaviorSubject<AppTab[]>([this.homeTab]);
  private readonly activeIndexSubject = new BehaviorSubject<number>(0);
  private readonly activeTabSubject = new BehaviorSubject<AppTab>(this.homeTab);

  readonly tabs$ = this.tabsSubject.asObservable();
  readonly activeIndex$ = this.activeIndexSubject.asObservable();
  readonly activeTab$ = this.activeTabSubject.asObservable();

  constructor() {
    this.restoreTabs();
  }

  // Called after StorageService.setIdentity() succeeds.
  // Initial navigation updates the visible browser URL, unlike normal tab switches.
  async startAuthenticatedSession(): Promise<boolean> {
    this.clearTabs();
    const navigated = await this.router.navigateByUrl(this.homeTab.url, { replaceUrl: true });
    if (navigated) this.saveTabs();
    return navigated;
  }

  /** Reload ke baad active tab ka component load karne ke liye (layout ngOnInit me call karo). */
  restoreActiveTab(): void {
    const index = this.activeIndexSubject.value;
    const tab = this.tabsSubject.value[index];
    if (!tab || this.sameUrl(tab.url, this.homeTab.url)) return;
    this.navigateToTab(index);
  }

  openMenuTab(menu: MenuItem): void {
    if (!menu.url) return;
    this.openTab({ title: menu.title, url: menu.url, closable: true });
  }

  openTab(tab: AppTab): void {
    const index = this.addOrSelectTab(tab);
    this.navigateToTab(index);
  }

  addOrSelectTab(tab: AppTab): number {
    const tabs = [...this.tabsSubject.value];
    const normalizedUrl = this.normalizeUrl(tab.url);
    const existingIndex = tabs.findIndex((item) => this.normalizeUrl(item.url) === normalizedUrl);

    if (existingIndex >= 0) {
      this.setActiveIndex(existingIndex);
      return existingIndex;
    }

    tabs.push(tab);
    this.tabsSubject.next(tabs);
    this.setActiveIndex(tabs.length - 1);
    this.saveTabs();
    return tabs.length - 1;
  }

  selectTab(index: number): void {
    if (!this.tabsSubject.value[index]) return;
    this.setActiveIndex(index);
    this.navigateToTab(index);
  }

  /**
   * `activateLast` is opt-in so existing callers retain the original behavior.
   * User Editor uses it to return to the last remaining tab with one navigation.
   */
  closeTab(index: number, activateLast = false): void {
    const tabs = [...this.tabsSubject.value];
    const tab = tabs[index];

    if (!tab || tab.closable === false) return;

    const currentIndex = this.activeIndexSubject.value;
    tabs.splice(index, 1);
    this.tabsSubject.next(tabs);

    let nextIndex = currentIndex;
    if (index < currentIndex) nextIndex = currentIndex - 1;
    if (index === currentIndex) {
      nextIndex = activateLast ? tabs.length - 1 : Math.min(index, tabs.length - 1);
    }
    if (nextIndex < 0) nextIndex = 0;

    this.setActiveIndex(nextIndex);
    this.saveTabs();

    if (index === currentIndex) this.navigateToTab(nextIndex);
  }

  /** Snapshot of the tab list; no subscription or mutable state in feature components. */
  tabsSnapshot(): readonly AppTab[] {
    return this.tabsSubject.value;
  }

  closeOthers(index: number): void {
    const selectedTab = this.tabsSubject.value[index];
    if (!selectedTab) return;

    const fixedTabs = this.tabsSubject.value.filter((tab) => tab.closable === false);
    const alreadyFixed = fixedTabs.some((tab) => this.sameUrl(tab.url, selectedTab.url));
    const tabs = alreadyFixed ? fixedTabs : [...fixedTabs, selectedTab];
    const nextIndex = tabs.findIndex((tab) => this.sameUrl(tab.url, selectedTab.url));

    this.tabsSubject.next(tabs);
    this.setActiveIndex(Math.max(nextIndex, 0));
    this.saveTabs();
    this.navigateToTab(Math.max(nextIndex, 0));
  }

  closeLeft(index: number): void {
    const currentTabs = this.tabsSubject.value;
    const selectedTab = currentTabs[index];
    if (!selectedTab) return;

    const tabs = currentTabs.filter((tab, tabIndex) => tab.closable === false || tabIndex >= index);
    const nextIndex = tabs.findIndex((tab) => this.sameUrl(tab.url, selectedTab.url));

    this.tabsSubject.next(tabs);
    this.setActiveIndex(Math.max(nextIndex, 0));
    this.saveTabs();
    this.navigateToTab(Math.max(nextIndex, 0));
  }

  closeRight(index: number): void {
    const currentTabs = this.tabsSubject.value;
    const selectedTab = currentTabs[index];
    if (!selectedTab) return;

    const tabs = currentTabs.filter((tab, tabIndex) => tab.closable === false || tabIndex <= index);
    const nextIndex = tabs.findIndex((tab) => this.sameUrl(tab.url, selectedTab.url));

    this.tabsSubject.next(tabs);
    this.setActiveIndex(Math.max(nextIndex, 0));
    this.saveTabs();
    this.navigateToTab(Math.max(nextIndex, 0));
  }

  syncTabsWithMenu(menus: MenuItem[]): void {
    const allowedUrls = this.collectUrls(menus);
    const currentTabs = this.tabsSubject.value;
    const activeTab = currentTabs[this.activeIndexSubject.value];

    const tabs = currentTabs.filter((tab) => {
      if (tab.closable === false) return true;
      return (
        allowedUrls.some((url) => this.urlBelongsTo(tab.url, url)) ||
        this.invoiceChildTabAllowed(tab.url, allowedUrls) ||
        this.userChildTabAllowed(tab.url, allowedUrls)
      );
    });

    if (!tabs.some((tab) => this.sameUrl(tab.url, this.homeTab.url))) tabs.unshift(this.homeTab);

    this.tabsSubject.next(tabs);

    const activeIndex = activeTab
      ? tabs.findIndex((tab) => this.sameUrl(tab.url, activeTab.url))
      : 0;
    this.setActiveIndex(activeIndex >= 0 ? activeIndex : 0);
    this.saveTabs();
    if (activeTab && activeIndex < 0) this.navigateToTab(0);
  }

  clearTabs(): void {
    this.tabsSubject.next([this.homeTab]);
    this.setActiveIndex(0);
    localStorage.removeItem(this.storageKey);
  }

  getShareUrl(index: number): string | null {
    const tab = this.tabsSubject.value[index];
    if (!tab) return null;
    return `${window.location.origin}${tab.url}`;
  }

  canCloseLeft(index: number): boolean {
    return this.tabsSubject.value.some(
      (tab, tabIndex) => tabIndex < index && tab.closable !== false,
    );
  }

  canCloseRight(index: number): boolean {
    return this.tabsSubject.value.some(
      (tab, tabIndex) => tabIndex > index && tab.closable !== false,
    );
  }

  private setActiveIndex(index: number): void {
    const tabs = this.tabsSubject.value;
    const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));
    const tab = tabs[safeIndex] ?? this.homeTab;

    this.activeIndexSubject.next(safeIndex);
    this.activeTabSubject.next(tab);
    this.saveTabs();
  }

  private navigateToTab(index: number): void {
    const tab = this.tabsSubject.value[index];
    if (!tab) return;
    void this.router.navigateByUrl(tab.url, { skipLocationChange: true });
  }

  private collectUrls(menus: MenuItem[]): string[] {
    const urls: string[] = [];

    for (const menu of menus) {
      if (menu.url) urls.push(this.normalizeUrl(menu.url));
      if (menu.children?.length) urls.push(...this.collectUrls(menu.children));
    }

    return urls;
  }

  /** Invoice list/menu permission covers its workflows, not unrelated routes. */
  private invoiceChildTabAllowed(tabUrl: string, allowedUrls: string[]): boolean {
    const path = this.normalizePath(tabUrl);
    const invoiceListAuthorized = allowedUrls.some((url) =>
      ['/invoicing', '/invoicing/invoice-list'].includes(this.normalizePath(url)),
    );
    if (!invoiceListAuthorized) return false;

    return (
      [
        '/invoicing/create-invoice',
        '/invoicing/payment-details',
        '/invoicing/group-properties',
      ].includes(path) || /^\/invoicing\/(edit-invoice|invoice-details)\/\d+$/.test(path)
    );
  }

  /** An authorized User List menu also covers its add/edit workflow tabs. */
  private userChildTabAllowed(tabUrl: string, allowedUrls: string[]): boolean {
    const allowed = allowedUrls.some((url) =>
      ['/user', '/user/list'].includes(this.normalizePath(url)),
    );
    if (!allowed) return false;

    return /^\/user\/(?:manage|[1-9]\d*)$/.test(this.normalizePath(tabUrl));
  }

  private urlBelongsTo(tabUrl: string, menuUrl: string): boolean {
    const tab = this.normalizePath(tabUrl);
    const menu = this.normalizePath(menuUrl);
    return tab === menu || tab.startsWith(`${menu}/`);
  }

  private sameUrl(first: string, second: string): boolean {
    return this.normalizeUrl(first) === this.normalizeUrl(second);
  }

  private normalizeUrl(url: string): string {
    const cleanUrl = url.split('#')[0];
    return cleanUrl.length > 1 ? cleanUrl.replace(/\/+$/, '') : cleanUrl;
  }

  private normalizePath(url: string): string {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return cleanUrl.length > 1 ? cleanUrl.replace(/\/+$/, '') : cleanUrl;
  }

  private saveTabs(): void {
    const value: StoredTabs = {
      tabs: this.tabsSubject.value,
      activeIndex: this.activeIndexSubject.value,
    };

    localStorage.setItem(this.storageKey, JSON.stringify(value));
  }

  private restoreTabs(): void {
    const storedValue = localStorage.getItem(this.storageKey);
    if (!storedValue) return;

    try {
      const parsed = JSON.parse(storedValue) as StoredTabs | AppTab[];
      const storedTabs = Array.isArray(parsed) ? parsed : parsed.tabs;
      const storedIndex = Array.isArray(parsed) ? 0 : parsed.activeIndex;
      const tabs = storedTabs?.length ? [...storedTabs] : [this.homeTab];

      if (!tabs.some((tab) => this.sameUrl(tab.url, this.homeTab.url))) tabs.unshift(this.homeTab);

      this.tabsSubject.next(tabs);
      this.setActiveIndex(storedIndex ?? 0);
    } catch {
      localStorage.removeItem(this.storageKey);
      this.tabsSubject.next([this.homeTab]);
      this.setActiveIndex(0);
    }
  }
}
